import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, ViewEncapsulation, computed, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogClose } from '@angular/material/dialog';
import { MatDivider } from '@angular/material/divider';

import { DialogResult } from '@shared/features/dialog/data-access/result.types';
import { ConfirmationConfig } from '@shared/features/dialog/features/confirmation-dialog/data-access/confirmation.types';

import { IconComponent } from '@ui-kit/components/base/icon/icon.component';

@Component({
  selector: 'app-confirmation-dialog',
  template: `
    <div class="relative flex h-full flex-col">
      <!-- Header -->

      <div class="inline-flex h-16 w-full items-center justify-center gap-3 p-5">
        <div class="flex items-center justify-center gap-3 rounded-md border border-slate-200 p-1 shadow">
          <app-icon [icon]="data.icon?.name!" [iconClass]="iconClasses()" />
        </div>
        <div class="shrink grow basis-0 text-xl font-medium text-gray-950">
          {{ data.title }}
        </div>
        <!-- Dismiss button -->
        @if (data.dismissible) {
          <button mat-icon-button [matDialogClose]="DialogResult.cancelled()">
            <app-icon icon="tabler-x" iconClass="text-gray-900" />
          </button>
        }
      </div>

      <mat-divider />

      @if (data.subTitle || data.message) {
        <div class="px-5 py-6">
          <div class="flex flex-col items-start justify-start gap-4">
            @if (data.subTitle) {
              <div class="text-base font-medium leading-normal text-slate-800">
                {{ data.subTitle }}
              </div>
            }

            @if (data.message) {
              <div
                class="space-y-4 self-stretch text-sm font-normal leading-tight text-slate-600"
                [innerHTML]="data.message"></div>
            }
          </div>
        </div>
      }

      <mat-divider />

      <!-- Actions -->
      @if (data.actions?.confirm?.show || data.actions?.cancel?.show) {
        <mat-dialog-actions class="flex flex-col justify-end gap-2 px-4 py-6 sm:flex-row sm:gap-0">
          @if (data.actions?.cancel?.show) {
            <button
              mat-stroked-button
              class="w-full sm:w-auto"
              type="button"
              [matDialogClose]="DialogResult.cancelled()">
              {{ data.actions?.cancel?.label }}
            </button>
          }
          @if (data.actions?.confirm?.show) {
            <button
              mat-flat-button
              type="submit"
              class="!ml-0 w-full sm:!ml-2 sm:w-auto"
              [ngClass]="{
                '!bg-primary': data.actions?.confirm?.color === 'primary',
                '!bg-button-alert': data.actions?.confirm?.color === 'error',
                '!bg-button-confirm': data.actions?.confirm?.color === 'accept',
              }"
              [matDialogClose]="DialogResult.success()">
              {{ data.actions?.confirm?.label }}
            </button>
          }
        </mat-dialog-actions>
      }
    </div>
  `,
  styles: `
    .mat-mdc-dialog-container {
      .mat-mdc-dialog-surface {
        padding: 0 !important;
      }
    }
  `,
  encapsulation: ViewEncapsulation.None,
  imports: [IconComponent, MatDivider, MatButtonModule, MatDialogClose, NgClass, MatDialogActions],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfirmationDialogComponent {
  data: ConfirmationConfig = inject<ConfirmationConfig>(MAT_DIALOG_DATA);
  protected readonly DialogResult = DialogResult;

  protected readonly iconClasses = computed(() => {
    const color = this.data.icon?.color;
    if (color === 'primary') return 'text-primary';
    if (color === 'error') return 'text-red-500';
    if (color === 'accept') return 'text-button-confirm';
    return 'text-primary';
  });
}
